/**
 * CRO cart drawer behaviors (cart-drawer split test).
 *
 * Everything here is additive: the production Alpine stores/components in
 * alpine-store.js / alpine-data.js are untouched. This file defines:
 *
 * - croCart          drawer shell behaviors: instant body scroll lock (the
 *                    production `global` watcher waits 1s), Android/iOS back
 *                    button closes the drawer instead of leaving the page
 *                    (mobile only), footer height -> --cro-cart-footer-h so
 *                    the scroll area clears ShipInsure's injected widget,
 *                    remove-with-undo, and the in-drawer variant edit sheet.
 * - cartDrawerUpsellsCro   same contract as cartDrawerUpsells but shows up
 *                    to 6 candidates for the horizontal rail (production
 *                    grid caps at 2).
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('cartDrawerUpsellsCro', (productIds = []) => ({
    productIds: productIds.map((productId) => String(productId)),
    get cartProductIds() {
      return Alpine.store('cart').cartItems.map((item) => String(item.product_id));
    },
    get visibleProductIds() {
      if (!this.productIds || !this.cartProductIds) {
        return [];
      }
      return this.productIds
        .filter((productId) => !this.cartProductIds.includes(productId))
        .slice(0, 6);
    },
    isProductVisible(productId) {
      return this.visibleProductIds.includes(String(productId));
    }
  }));

  Alpine.data('croCart', () => ({
    croUndoVisible: false,
    croUndoCanRestore: false,
    croUndoItem: null,
    croUndoTimer: null,

    croEditOpen: false,
    croEditBusy: false,
    croEditKey: null,
    croEditQty: 1,

    _croHistoryArmed: false,
    _croPopClosing: false,
    _croFooterObserver: null,

    get croCartOpen() {
      return Alpine.store('cart').isSideCartOpen;
    },

    init() {
      this.$watch('croCartOpen', (open) => {
        // Immediate scroll lock — production's `global` watcher does the same
        // thing on a 1s delay, which lets the page scroll behind the drawer.
        if (!Alpine.store('cart').showUpdateItemPopup) {
          Alpine.store('screen').isScrollableBody = !open;
        }

        if (!open) {
          this.croDismissUndo();
          if (this.croEditOpen) this.croCloseEdit();
        }

        this.croSyncHistory(open);
      });

      window.addEventListener('popstate', () => {
        if (!this._croHistoryArmed || !this.croIsMobile() || !Alpine.store('cart').isSideCartOpen) return;

        // Back closes the topmost layer: edit sheet or quick-add first, the
        // drawer only when nothing sits above it. Closing a layer re-arms the
        // history entry so the next back press closes the drawer.
        if (this.croEditOpen) {
          this.croCloseEdit();
          try { history.pushState({ croCartOpen: true }, ''); } catch (e) {}
          return;
        }
        if (Alpine.store('cart').showMobileQuickAddPopup) {
          const openQuickAdd = document.querySelector('body > div.fixed.z-50.block');
          const closeControl = openQuickAdd && openQuickAdd.querySelector('[\\@click*="closeMobileQuickAdd"]');
          if (closeControl) {
            closeControl.click();
            try { history.pushState({ croCartOpen: true }, ''); } catch (e) {}
            return;
          }
          // fall through and close the drawer if the popup can't be found
        }

        this._croPopClosing = true;
        this._croHistoryArmed = false;
        Alpine.store('cart').closeCart();
      });

      this.croObserveFooter();
    },

    croIsMobile() {
      return window.matchMedia('(max-width: 767px)').matches;
    },

    // Back button closes the full-screen drawer on mobile instead of leaving
    // the page. Guarded so an X-tap close consumes the pushed history entry.
    croSyncHistory(open) {
      if (!this.croIsMobile()) return;
      try {
        if (open && !this._croHistoryArmed) {
          history.pushState({ croCartOpen: true }, '');
          this._croHistoryArmed = true;
        } else if (!open && this._croHistoryArmed) {
          this._croHistoryArmed = false;
          if (this._croPopClosing) {
            this._croPopClosing = false;
          } else {
            history.back();
          }
        }
      } catch (e) {
        // history APIs can throw in sandboxed webviews — drawer still works
      }
    },

    // The checkout footer's height changes when ShipInsure mounts its widget,
    // when sale banners render, etc. Track the real height so the scroll area
    // is never hidden behind it.
    croObserveFooter() {
      if (!window.ResizeObserver) return;
      const attach = () => {
        const footer = document.querySelector('.cart-drawer-subtotal-bottom');
        if (!footer) {
          setTimeout(attach, 500);
          return;
        }
        this._croFooterObserver = new ResizeObserver(() => {
          const height = Math.ceil(footer.getBoundingClientRect().height);
          if (height > 0) {
            document.documentElement.style.setProperty('--cro-cart-footer-h', height + 'px');
          }
        });
        this._croFooterObserver.observe(footer);
      };
      attach();
    },

    // ---- Remove with undo (replaces the "Are You Sure?" popup) ----

    croRemoveItem(item) {
      if (!item || Alpine.store('cart').isCartQtyInProgress) return;
      const attributes = (item.properties && item.properties.attributes) || {};

      // Offer-engine items are removed directly but NOT restorable via undo:
      // re-adding them manually would bypass how the offer created them.
      const restorable = !(
        attributes._is_free_shirt
        || attributes.__fixed_price_offer
        || attributes.__is_member_product
        || attributes.__spend_based_offer
        || attributes.__winter_bundle_sale
        || attributes.__is_winter_product
        || attributes._bb_upgrade_kit
        || attributes._bb_upgrade_shirt
        || attributes._fwp_second_product
        || attributes.__incremental_sale
        || attributes.__buy_more_get_free
        || attributes._is_absolute_free_product
        || attributes._is_funnel_product
      );

      this.croUndoItem = restorable
        ? {
            id: item.variant_id,
            quantity: item.quantity,
            properties: attributes,
            selling_plan: item.selling_plan && item.selling_plan.id ? item.selling_plan.id : null
          }
        : null;
      this.croUndoCanRestore = restorable;

      Alpine.store('cart').removeItem(item.key);
      this.croShowUndo();
    },

    croShowUndo() {
      this.croUndoVisible = true;
      clearTimeout(this.croUndoTimer);
      this.croUndoTimer = setTimeout(() => {
        this.croUndoVisible = false;
        this.croUndoItem = null;
      }, 6000);
    },

    croDismissUndo() {
      clearTimeout(this.croUndoTimer);
      this.croUndoVisible = false;
      this.croUndoItem = null;
    },

    croUndoRemove() {
      if (!this.croUndoItem || Alpine.store('cart').isCartQtyInProgress) return;
      const item = {
        id: Number(this.croUndoItem.id),
        quantity: this.croUndoItem.quantity,
        properties: this.croUndoItem.properties
      };
      if (this.croUndoItem.selling_plan) {
        item.selling_plan = this.croUndoItem.selling_plan;
      }
      this.croDismissUndo();
      // addItems wraps properties into { attributes } itself — same call the
      // production edit flow makes, so offer pricing re-applies.
      Alpine.store('cart').addItems([item], null, true, false);
    },

    // ---- In-drawer variant edit (cart stays open behind the sheet) ----

    croEditItem(productHandle, editItemKey, editItemQty) {
      if (Alpine.store('cart').isCartQtyInProgress) return;
      const cartItem = Alpine.store('cart').cartItems.find((c) => c.key == editItemKey);
      if (!cartItem) return;

      this.croEditKey = editItemKey;
      this.croEditQty = editItemQty;
      this.croEditBusy = false;
      this.croEditOpen = true;

      // Parity with editItem() in alpine-data.js — the fetched cart-edit view
      // reads editItemDetails for its pricing UI.
      Alpine.store('cart').editItemDetails = {
        key: editItemKey,
        qty: editItemQty,
        price: {
          compare_at_price: cartItem.compare_at_price / 100,
          final_price: cartItem.final_price / 100,
          original_price: cartItem.original_price / 100
        }
      };

      const container = document.getElementById('cro_edit_product_info');
      if (container) {
        container.innerHTML = '<div class="cro-edit-loading">Loading&hellip;</div>';
      }

      const selectedVariantId = String(editItemKey).split(':')[0];
      fetch(`/products/${productHandle}?view=cart-edit&variant=${selectedVariantId}`)
        .then((response) => response.text())
        .then((html) => {
          if (this.croEditOpen && container) {
            container.innerHTML = html;
          }
        })
        .catch((e) => {
          console.error(e);
          if (container) {
            container.innerHTML = '<p class="text-sm text-black">Something went wrong. Please close and try again.</p>';
          }
        });
    },

    croCloseEdit() {
      this.croEditOpen = false;
      this.croEditBusy = false;
      const container = document.getElementById('cro_edit_product_info');
      if (container) container.innerHTML = '';
    },

    croUpdateItem() {
      if (this.croEditBusy) return;

      // Scoped to the sheet — document-wide querySelector('variant-selects')
      // would grab the main PDP's picker on product pages.
      const variantSelects = document.querySelector('#CroCartEditSheet variant-selects');
      if (!variantSelects || !variantSelects.currentVariant) {
        this.croCloseEdit();
        return;
      }

      const selectedItem = Alpine.store('cart').cartItems.find((i) => i.key == this.croEditKey);
      if (!selectedItem) {
        this.croCloseEdit();
        return;
      }

      if (variantSelects.currentVariant.id == selectedItem.variant_id) {
        this.croCloseEdit();
        return;
      }

      this.croEditBusy = true;

      const item = {
        id: variantSelects.currentVariant.id,
        quantity: this.croEditQty,
        properties: selectedItem.properties.attributes
      };
      if (selectedItem.selling_plan && selectedItem.selling_plan.id) {
        item.selling_plan = selectedItem.selling_plan.id;
      }

      Alpine.store('cart').removeAndAddItem(this.croEditKey, item, () => {
        this.croCloseEdit();
      });
    }
  }));
});
